import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { Group, Image, Transformer, Text } from 'react-konva'
import useImage from 'use-image'

const RectangleBoxLogo = ({
  shapeProps,
  isSelected,
  onChange,
  urlImg,
  scale = 1,
  signerInfo = '',
}) => {
  const [image] = useImage(urlImg)
  const groupRef = useRef(null)
  const imageRef = useRef(null)
  const textRef = useRef(null)
  const trRef = useRef(null)

  // Attach Transformer when selected or props change
  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current])
      trRef.current.getLayer().batchDraw()
    }
  }, [isSelected, shapeProps])

  // Cache image for performance
  useLayoutEffect(() => {
    if (imageRef.current) imageRef.current.cache()
  }, [shapeProps, image, isSelected])

  // Handle Group drag
  const handleDragEnd = useCallback(
    e => {
      const node = groupRef.current
      onChange({
        ...shapeProps,
        x: node.x() / scale,
        y: node.y() / scale,
      })
    },
    [onChange, shapeProps, scale]
  )

  // Handle Group transform (scale)
  const handleTransformEnd = useCallback(() => {
    const node = groupRef.current
    const newScaleX = node.scaleX()
    const newScaleY = node.scaleY()
    node.scaleX(1)
    node.scaleY(1)

    onChange({
      ...shapeProps,
      x: node.x() / scale,
      y: node.y() / scale,
      width: Math.max(5, shapeProps.width * newScaleX) / scale,
      height: Math.max(5, shapeProps.height * newScaleY) / scale,
    })
  }, [onChange, shapeProps, scale])

  // Text settings
  const fontSize = 8 // Constant font size
  const textHeight = fontSize * 1.2

  return (
    <>
      <Group
        ref={groupRef}
        x={shapeProps.x * scale}
        y={shapeProps.y * scale}
        draggable
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}>
        <Image
          image={image}
          ref={imageRef}
          width={shapeProps.width * scale}
          height={shapeProps.height * scale}
          x={0}
          y={0}
          opacity={0.8}
        />
        {signerInfo && (
          <Text
            ref={textRef}
            text={signerInfo}
            x={shapeProps.width * scale + 5}
            y={(shapeProps.height * scale - textHeight) / 3.4}
            fontSize={8}
            fill="black"
            width={200}
            align="left"
            lineHeight={1.05}
          />
        )}
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          enabledAnchors={[
            'top-left',
            'top-right',
            'bottom-right',
            'bottom-left',
          ]}
          anchorSize={7}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 5 || newBox.height < 5) return oldBox
            return newBox
          }}
        />
      )}
    </>
  )
}

export default memo(RectangleBoxLogo)
